/**
 * Order-status normalization — the single legacy→canonical map. Stored docs
 * may still carry pre-Slice-1b values (OPEN/QUOTED/WORK_ORDER_SENT/CLOSED/LOST);
 * the repository read boundary runs every status through `normalizeOrderStatus`
 * so the app is correct on un-migrated data the moment the enum flips.
 *
 * NOTE: `migrateOrderStatuses` (the wire-tidy Cloud Function) is NOT a 1:1 copy
 * of this map. It applies the same renames for WORK_ORDER_SENT/CLOSED/LOST, but
 * for OPEN/QUOTED it additionally consults the payment ledger and splits them
 * into CONFIRMED (deposit met) vs AWAITING_DEPOSIT (not) — a distinction this
 * synchronous read boundary can't make (it has no DB handle, so it always maps
 * OPEN/QUOTED → AWAITING_DEPOSIT). Both are correct: the money CFs accept
 * OPEN/QUOTED/AWAITING_DEPOSIT interchangeably in their auto-advance gate, so an
 * un-migrated deposit-paid order reads here as AWAITING_DEPOSIT yet still
 * advances. Don't "simplify" one map to match the other — the asymmetry is
 * deliberate.
 */
import type { OrderStatus } from '../../../types';
import { LIFECYCLE_ORDER } from './lifecycle';

const LEGACY_ALIAS: Record<string, OrderStatus> = {
  OPEN: 'AWAITING_DEPOSIT',
  QUOTED: 'AWAITING_DEPOSIT',
  WORK_ORDER_SENT: 'IN_PRODUCTION',
  CLOSED: 'COMPLETED',
  LOST: 'CANCELLED',
};

const VALID = new Set<string>([...LIFECYCLE_ORDER, 'CANCELLED', 'ON_HOLD']);

/** Map any stored status string (legacy or current) to the canonical OrderStatus. */
export const normalizeOrderStatus = (raw: string): OrderStatus => {
  if (raw in LEGACY_ALIAS) return LEGACY_ALIAS[raw];
  if (VALID.has(raw)) return raw as OrderStatus;
  return 'AWAITING_DEPOSIT';
};

/** True when forward status `a` is at or beyond forward status `b`. Off-ramp
 *  statuses (CANCELLED/ON_HOLD, not in the forward order) are never "at least". */
export const stageAtLeast = (a: OrderStatus, b: OrderStatus): boolean => {
  const ai = LIFECYCLE_ORDER.indexOf(a);
  const bi = LIFECYCLE_ORDER.indexOf(b);
  return ai >= 0 && bi >= 0 && ai >= bi;
};
